import React, { memo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import styled, { css } from '@mint-lib/styled';
import { Link } from '@mint/routing';

import { Box } from '@myn/mui';
import { JourneyProviders } from '@myn/shared/src/constants';

import { useUpdateMediaPlanRowOldMutation } from '../../../../../../api/mediaPlansApiSlice.js';
import { BUILDER_ROW_TYPES } from '../../../../../../constants/campaignBuilder.js';
import { useInsertionOrderApi } from '../../../../../../hooks/useInsertionOrderApi.js';
import { useInsertionOrderLoading } from '../../../../../../hooks/useInsertionOrderLoading.js';
import { useSelectedBuyingPlanId } from '../../../../../../hooks/useSelectedBuyingPlanId.js';
import { TooltipConditions } from '../../../../../../types/insertionOrder.js';
import { getLink } from '../../../../../../utils/campaigns/actionsField.js';
import { getInsertionOrderButtonTooltip } from '../../../../../../utils/campaignsBuilder.js';
import {
  BuilderTableColumn,
  BuilderTableRow,
  SETTINGS_STATUS,
} from '../../../../types.js';
import ActionItem from './ActionItem.jsx';

type Props = {
  row: BuilderTableRow;
  column: BuilderTableColumn;
};

const DISPLAY_ADWORDS_PLATFORM_MEDIA = [
  'google_display_network_integrated',
  'google_display_network',
  // 'discovery_ads', TODO uncomment if i'm a display-adwords integration
  'adwords',
];
const YOUTUBE_GOOGLE_ADS_PLATFORM_MEDIA = [JourneyProviders.VIDEO_ADWORDS];

const ActionsField = memo(({ row, column }: Props) => {
  const { t } = useTranslation('@myn/planning');

  const selectedMediaPlanId = useSelectedBuyingPlanId();

  const isAllowedSettings = useIsAllowedPermission(
    'campaignSettingsFeature:view',
  );
  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const isNonFinancialUser = !useIsAllowedPermission(
    'financialDataFeature:view',
  );
  const isUserReadOnlyAccess = !useIsAllowedPermission(
    'campaignPlanningFeature:modify',
  );
  const isIoReadOnlyAccess = !useIsAllowedPermission(
    'insertionOrderFeature:modify',
  );

  const [updateMediaPlanRowMutation] = useUpdateMediaPlanRowOldMutation();

  const {
    id,
    type,
    campaign,
    channel,
    platformMedia,
    dateFrom,
    dateTo,
    budget,
    name,
    currencyValue,
    isFromRecommendations,
    parentInfo,
    settingsStatusMessage,
    canBeDeleted,
    subRows,
    supportNotifications,
    hasIntegration,
    isConfiguredForIo: isIoCanBeSent,
    isBuyingPlanLocked,
    isCampaignCurrencyMode,
    isPredictivePlanningMode,
    isValidIoGroup,
    userIoGroupId,
    integratedPlatforms,
    platformByName,
    scale,
    isBuyingPlanApproved,
    isArchivedVersion,
  } = row.original;

  const isTotalsRow = type === BUILDER_ROW_TYPES.TOTAL;
  const isSettingsRow = type === BUILDER_ROW_TYPES.TILE;
  const isConfiguredRow = dateFrom && dateTo && budget;
  const isPlatformLevel =
    type === BUILDER_ROW_TYPES.PLATFORM ||
    type === BUILDER_ROW_TYPES.MEDIA_NETWORK;
  const isMediaRowLevel = type === BUILDER_ROW_TYPES.TILE;

  const { handleInsertionOrderInfoRequest } = useInsertionOrderApi();

  const { isLoading: isInsertionOrderLoading } = useInsertionOrderLoading();
  const campaignId = column.campaign?.id;
  const showSettingsButton = !supportNotifications && isSettingsRow;
  const isSettingsDisabled =
    !isSettingsRow ||
    !campaign ||
    isPredictivePlanningMode ||
    !isAllowedSettings ||
    !campaignId;

  const hasAlerts =
    !!settingsStatusMessage &&
    settingsStatusMessage === SETTINGS_STATUS.NO_CAMPAIGNS;

  const showAlerts = hasAlerts && !supportNotifications;

  const showChangeCurrencyButton = isSettingsRow && !isNonFinancialUser;

  const isCurrencyButtonDisabled =
    hasIntegration ||
    isCampaignCurrencyMode ||
    !isConfiguredRow ||
    isUserReadOnlyAccess ||
    !isAllowed ||
    isPredictivePlanningMode ||
    isArchivedVersion ||
    isBuyingPlanLocked;

  const currencyButtonTooltip =
    !isCurrencyButtonDisabled ||
    (isCurrencyButtonDisabled && (!isConfiguredRow || isArchivedVersion))
      ? t('Set media row currency')
      : t('Currency change disabled for this media row.');

  const hasSubRows = subRows.length !== 0;
  const showIOButton = isPlatformLevel || isMediaRowLevel;
  const isPlatformLevelWithoutMediaRows = isPlatformLevel && !hasSubRows;

  const isIOButtonDisabled =
    isIoReadOnlyAccess ||
    !isIoCanBeSent ||
    isPlatformLevelWithoutMediaRows ||
    isInsertionOrderLoading ||
    !isValidIoGroup ||
    !isBuyingPlanApproved;

  const isDeleteButtonDisabled =
    !isAllowed ||
    isPredictivePlanningMode ||
    isArchivedVersion ||
    !canBeDeleted ||
    isBuyingPlanLocked;

  const isOmni = platformMedia
    ? platformByName?.[platformMedia]?.isOmni
    : undefined;

  const paramsObject = {
    tileId: campaign,
    channel: channel,
    platformMedia: platformMedia ?? '',
    integratedPlatforms,
    campaignId,
    isGhost: parentInfo.isGhost,
    isOmni,
    selectedMediaPlanId: selectedMediaPlanId ?? undefined,
  };

  const settingsLink = getLink(paramsObject);

  const handleRemoveClick = () => {
    if (isAllowed && !isPredictivePlanningMode) {
      column.onRemoveClick({
        rowId: id,
        hasChildren: row.canExpand,
        type,
        campaign,
      });
    }
  };

  const handleSettingsClick = (e: React.MouseEvent<any>) => {
    if (isSettingsDisabled) {
      e.preventDefault();
      return;
    }

    if (isFromRecommendations) {
      updateMediaPlanRowMutation({
        rowId: id,
        isFromRecommendations: false,
      });
    }
  };

  const handleChangeRowCurrencyClick = () => {
    if (
      isConfiguredRow &&
      !hasSubRows &&
      isAllowed &&
      !isPredictivePlanningMode
    ) {
      column.onChangeRowCurrencyClick({
        rowId: id,
        rowCurrency: currencyValue,
        rowName: name ?? '',
        startDate: dateFrom,
        endDate: dateTo,
      });
    }
  };

  const handleContextMenu = (e: React.MouseEvent<any>) => {
    if (isSettingsDisabled) {
      e.preventDefault();
    }
  };

  const handleInsertionOrderClick = () => {
    if (!selectedMediaPlanId) {
      return;
    }

    handleInsertionOrderInfoRequest({
      buyingPlanId: selectedMediaPlanId,
      buyingPlanItemId: id,
      isPlatformLevel,
    });
  };

  const handleInsertionOrderTooltip = () => {
    const tooltipConditions: TooltipConditions = {
      hasSubRows,
      isIoCanBeSent,
      isPlatformLevel,
      isLoading: isInsertionOrderLoading,
      isValidIoGroup,
      isBuyingPlanApproved: isBuyingPlanApproved,
      isUserPartOfIoGroup: !!userIoGroupId,
      isReadOnlyUser: !isAllowed || isIoReadOnlyAccess,
    };
    return getInsertionOrderButtonTooltip(tooltipConditions, t);
  };

  if (isTotalsRow) {
    return null;
  }

  return (
    <Container>
      <Wrapper>
        {showAlerts ? (
          <ActionItem
            scale={scale}
            title={t('Media row is without campaigns')}
            icon="Warning"
          />
        ) : null}
        {showIOButton ? (
          <ActionItem
            scale={scale}
            title={handleInsertionOrderTooltip()}
            testId="insertion-order-button"
            icon="DocumentAdd"
            disabled={isIOButtonDisabled}
            onClick={handleInsertionOrderClick}
          />
        ) : null}
        {showSettingsButton ? (
          <Link
            {...settingsLink}
            onClick={handleSettingsClick}
            onContextMenu={handleContextMenu}
          >
            <ActionItem
              scale={scale}
              title={t('Configuration')}
              icon="SettingsAdjust"
              disabled={isSettingsDisabled}
            />
          </Link>
        ) : null}
        {showChangeCurrencyButton ? (
          <ActionItem
            scale={scale}
            title={currencyButtonTooltip}
            testId="currency-button"
            icon="Money"
            disabled={isCurrencyButtonDisabled}
            onClick={handleChangeRowCurrencyClick}
          />
        ) : null}
        {canBeDeleted ? (
          <ActionItem
            scale={scale}
            title={t('Delete')}
            icon="Delete"
            disabled={isDeleteButtonDisabled}
            onClick={handleRemoveClick}
          />
        ) : null}
      </Wrapper>
    </Container>
  );
});

const Wrapper = styled(Box)`
  ${({ theme }) => css`
    position: absolute;
    right: 0;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 ${theme.spacing(0.25)};
    background-color: ${theme.palette.ui.hoverRow};

    :before {
      content: '';
      position: absolute;
      left: -16px;
      height: 100%;
      width: 16px;

      background-image: linear-gradient(
        90deg,
        transparent 0%,
        ${theme.palette.ui.secondary} 100%
      );
    }
  `}
`;

const Container = styled(Box)`
  position: relative;
  height: 100%;
`;

export default ActionsField;
